import { existsSync, readFileSync } from "node:fs";
import { relative, resolve } from "node:path";
import { globSync } from "glob";
import { extractFrontmatter } from "../../markdown.js";
import { extractCaiSection } from "../../utils/merge.js";
import type { DriftIssue } from "../../types.js";

/**
 * Validate that `globs:` frontmatter in generated rule files still resolves to
 * real paths. A rule only fires when an agent touches a matching file, so a rule
 * whose globs ALL match zero files silently never fires — e.g. a `packages/**`
 * rule after the packages/ dir was renamed or removed.
 *
 * Flags per-rule, not per-glob: a rule with several selectors still fires as long
 * as one matches, so we only warn when every glob is dead (which keeps CAI's own
 * deliberately broad scaffold globs like `docker-compose*`/`deploy/**` from
 * generating noise on projects that simply lack those dirs).
 *
 * Deterministic, no AI, no network. Extends drift detection to the rule layer,
 * which no surveyed tool covers.
 */
export function checkRuleGlobs(projectRoot: string): DriftIssue[] {
  const ruleDirs = [
    { dir: resolve(projectRoot, ".claude", "rules"), ext: "md" },
    { dir: resolve(projectRoot, ".cursor", "rules"), ext: "mdc" },
  ];

  const issues: DriftIssue[] = [];

  for (const { dir, ext } of ruleDirs) {
    if (!existsSync(dir)) continue;
    const ruleFiles = globSync(`*.${ext}`, { cwd: dir, absolute: true, ignore: ["node_modules/**"] });

    for (const ruleFile of ruleFiles) {
      const globs = readRuleGlobs(ruleFile);
      if (globs.length === 0) continue;

      const dead = globs.filter(
        (pattern) =>
          globSync(pattern, {
            cwd: projectRoot,
            ignore: ["node_modules/**", ".git/**"],
            dot: false,
          }).length === 0
      );

      // Only flag when EVERY glob is dead — a rule with one live selector still fires.
      if (dead.length === globs.length) {
        const source = relative(projectRoot, ruleFile);
        const list = dead.map((p) => `"${p}"`).join(", ");
        issues.push({
          code: "DEAD_RULE_GLOB",
          severity: "warning",
          file: source,
          line: null,
          message: `Rule globs (${list}) match no files — this rule never fires`,
          explanation:
            "A path-scoped rule only loads when an agent touches a matching file. When all of a rule's globs resolve to zero files it is dead scoping, usually after the target directory was renamed or deleted.",
          suggestion: `Update the globs in ${source} to a path that exists, or remove the rule if it is obsolete.`,
        });
      }
    }
  }

  return issues;
}

/**
 * Read a rule file's `globs:` frontmatter. Generated rules wrap their content in
 * `<!-- cai:start -->` markers, which push the `---` off document start where
 * remark-frontmatter expects it — so unwrap the CAI section before parsing.
 */
function readRuleGlobs(ruleFile: string): string[] {
  let raw: string;
  try {
    raw = readFileSync(ruleFile, "utf-8");
  } catch {
    return [];
  }
  const content = extractCaiSection(raw) ?? raw;
  const fm = extractFrontmatter(content);
  return normalizeGlobs(fm?.globs);
}

/** Frontmatter `globs:` may be a YAML list or a comma-separated string. */
function normalizeGlobs(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === "string" && v.trim().length > 0).map((v) => v.trim());
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
  }
  return [];
}
